import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { styled, alpha } from '@mui/material/styles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';

interface AvatarUploadProps {
  currentAvatar?: string | null;
  initials?: string;
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => void;
  size?: number;
  disabled?: boolean;
}

const AvatarContainer = styled(Box)({
  position: 'relative',
  display: 'inline-block',
});

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: number }>(({ theme, size = 80 }) => ({
  width: size,
  height: size,
  fontSize: `${size * 0.4}px`,
  fontWeight: 600,
  fontFamily: '"IBM Plex Mono", monospace',
  backgroundColor: alpha(theme.palette.text.primary, 0.08),
  color: theme.palette.text.primary,
  border: `2px solid ${theme.palette.surface.glassBorder}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: theme.palette.success.main,
  },
}));

const UploadOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  cursor: 'pointer',

  '&:hover': {
    opacity: 1,
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  width: 28,
  height: 28,
  boxShadow: theme.shadows[2],

  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },

  '& svg': {
    fontSize: 16,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

export function AvatarUpload({
  currentAvatar,
  initials,
  onUpload,
  onDelete,
  size = 80,
  disabled = false,
}: AvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setLoading(true);
    try {
      await onUpload(file);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      setPreview(null);
    } finally {
      setLoading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      setPreview(null);
      onDelete();
    }
  };

  const avatarSrc = preview || currentAvatar || undefined;

  return (
    <AvatarContainer>
      <StyledAvatar src={avatarSrc} size={size} onClick={handleClick}>
        {!avatarSrc && initials}
      </StyledAvatar>

      {!disabled && (
        <UploadOverlay onClick={handleClick}>
          {loading ? (
            <CircularProgress size={size * 0.3} sx={{ color: 'white' }} />
          ) : (
            <CameraAltIcon sx={{ fontSize: size * 0.3, color: 'white' }} />
          )}
        </UploadOverlay>
      )}

      {currentAvatar && onDelete && !loading && !disabled && (
        <DeleteButton size="small" onClick={handleDelete}>
          <DeleteIcon />
        </DeleteButton>
      )}

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        disabled={disabled || loading}
      />
    </AvatarContainer>
  );
}
